import { 
  Code, 
  Save, 
  ZoomIn, 
  Info, 
  Layers, 
  Database,
  Image as ImageIcon,
  Loader2,
  AlertCircle
} from "lucide-react"
import type { LayerExperience } from "../../types/layer-experience"
import type { MapWorkspaceSpriteRootSelectorConfig } from "../../contracts/map-workspace"
import { Label } from "../../internal/primitives/label"
import { Input } from "../../internal/primitives/input"
import { Switch } from "../../internal/primitives/switch"
import { Slider } from "../../internal/primitives/slider"
import { Separator } from "../../internal/primitives/separator"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../internal/primitives/accordion"
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "../../internal/primitives/select"

export interface AdvancedSectionProps {
  layer: LayerExperience
  onUpdate: (patch: Partial<LayerExperience>) => void
  spriteRootSelector?: MapWorkspaceSpriteRootSelectorConfig | null
}

export function AdvancedSection({ layer, onUpdate, spriteRootSelector }: AdvancedSectionProps) {
  return (
    <div className="flex flex-col gap-8 pb-10">
      {/* Rule-style Header: Identity & Master Toggle */}
      <header className="flex items-center justify-between p-4 rounded-xl border bg-card shadow-sm">
        <div className="flex flex-col gap-1.5 flex-1 max-w-md">
          <Label className="text-[10px] font-bold uppercase text-muted-foreground/70 tracking-wider">Layer Identity</Label>
          <Input 
            value={layer.label || ""} 
            onChange={(e) => onUpdate({ label: e.target.value })}
            className="h-9 font-bold text-sm bg-muted/5 border-transparent focus:bg-background transition-all"
            placeholder="Untitled Layer"
          />
        </div>
        
        <div className="flex items-center gap-3 pl-4 border-l">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/20 border">
            <span className="text-[10px] font-bold uppercase text-muted-foreground">Active</span>
            <Switch 
              checked={layer.visibility.default_visible} 
              onCheckedChange={(checked) => onUpdate({
                visibility: { ...layer.visibility, default_visible: checked }
              })}
            />
          </div>
        </div>
      </header>

      <div className="flex flex-col gap-8">
        {/* Session-level Sprite Root Selector */}
        {spriteRootSelector && (
          <section className="space-y-4">
            <div className="flex items-center gap-2 px-1">
              <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Session Configuration</span>
              <div className="h-px flex-1 bg-border/50" />
            </div>

            <div className="p-4 rounded-xl border bg-card shadow-sm space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                  <ImageIcon className="w-4 h-4" />
                </div>
                <div className="flex flex-col">
                  <h4 className="text-xs font-bold italic">Sprite Root</h4>
                  <p className="text-[10px] text-muted-foreground">Select the icon set for this workbench session</p>
                </div>
              </div>

              <div className="space-y-2">
                {spriteRootSelector.loading ? (
                  <div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/5 animate-pulse">
                    <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
                    <span className="text-[10px] font-medium text-muted-foreground">Loading sprite sets...</span>
                  </div>
                ) : spriteRootSelector.errorMessage ? (
                  <div className="flex items-center gap-2 p-3 rounded-lg border border-destructive/20 bg-destructive/5 text-destructive">
                    <AlertCircle className="w-3.5 h-3.5" />
                    <span className="text-[10px] font-bold uppercase tracking-tight">{spriteRootSelector.errorMessage}</span>
                  </div>
                ) : spriteRootSelector.options.length === 0 ? (
                  <div className="p-3 rounded-lg border bg-muted/5 border-dashed flex flex-col items-center justify-center gap-1">
                    <span className="text-[10px] font-medium text-muted-foreground">
                      {spriteRootSelector.unavailableMessage || "No sprite sets available"}
                    </span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Select 
                      value={spriteRootSelector.selectedSetId || "none"} 
                      onValueChange={(val) => {
                        if (val === "none") {
                          spriteRootSelector.onClearSprite?.()
                        } else {
                          spriteRootSelector.onSelectSprite?.(val)
                        }
                      }}
                    >
                      <SelectTrigger className="w-full h-9 text-xs bg-muted/5">
                        <SelectValue placeholder="Select a sprite set" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none" className="text-xs italic text-muted-foreground">No Sprite Root</SelectItem>
                        {spriteRootSelector.options.map((opt) => (
                          <SelectItem key={opt.id} value={opt.id} disabled={opt.disabled} className="text-xs">
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {spriteRootSelector.helperText && (
                      <p className="text-[9px] text-muted-foreground px-1">{spriteRootSelector.helperText}</p>
                    )}
                  </div>
                )}
                <div className="p-2.5 rounded-lg bg-muted/20 border border-dashed flex items-start gap-2.5">
                  <Info className="w-3 h-3 mt-0.5 text-muted-foreground" />
                  <p className="text-[9px] leading-relaxed text-muted-foreground font-medium">
                    This setting applies to the entire map or custom-map session. The selected sprite root powers icon browsing and symbol styling in the <span className="text-foreground font-bold">Style</span> tab for all layers.
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Logic Section: Visibility Controls */}
        <section className="space-y-4">
          <div className="flex items-center gap-2 px-1">
            <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Logic</span>
            <div className="h-px flex-1 bg-border/50" />
          </div>

          <div className="relative pl-4">
            <div className="absolute left-0 top-0 bottom-0 w-px bg-primary/20" />
            <div className="absolute left-[-12px] top-4 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[10px] font-bold shadow-sm ring-4 ring-background">
              IF
            </div>
            
            <div className="flex flex-col gap-4 p-4 rounded-xl border bg-muted/5 border-primary/10 shadow-sm transition-all">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded bg-primary/10 text-primary">
                    <ZoomIn className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-bold">Zoom Level Visibility</span>
                    <span className="text-[10px] text-muted-foreground">Control when this layer renders on the map</span>
                  </div>
                </div>
                <div className="px-2 py-0.5 rounded text-[10px] font-mono bg-background border font-bold">
                  z{layer.visibility.min_zoom || 0} — z{layer.visibility.max_zoom || 22}
                </div>
              </div>

              <div className="px-2 py-4 space-y-4">
                <Slider 
                  min={0} 
                  max={22} 
                  step={1} 
                  value={[layer.visibility.min_zoom || 0, layer.visibility.max_zoom || 22]} 
                  onValueChange={([min, max]) => onUpdate({
                    visibility: { ...layer.visibility, min_zoom: min, max_zoom: max }
                  })}
                />
                <div className="flex justify-between text-[9px] text-muted-foreground uppercase font-bold px-1">
                  <span>z0 (Global)</span>
                  <span>z22 (Street)</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Effect Section: Layer Configuration */}
        <section className="space-y-4">
          <div className="flex items-center gap-2 px-1">
            <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Effect</span>
            <div className="h-px flex-1 bg-border/50" />
          </div>

          <div className="p-6 rounded-xl border bg-card/50 shadow-sm space-y-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                  <Save className="w-4 h-4" />
                </div>
                <div className="flex flex-col">
                  <h4 className="text-xs font-bold">Saved Views Integration</h4>
                  <p className="text-[10px] text-muted-foreground">Allow users to bookmark this layer state</p>
                </div>
              </div>
              <Switch 
                checked={layer.presets?.include_in_saved_views || false} 
                onCheckedChange={(checked) => onUpdate({
                  presets: { ...layer.presets, include_in_saved_views: checked }
                })}
              />
            </div>

            <Separator className="opacity-50" />

            <div className="grid gap-6">
              <div className="flex flex-col gap-1.5">
                <Label className="text-[10px] font-bold uppercase text-muted-foreground/70 tracking-tight">Technical Description</Label>
                <textarea 
                  className="min-h-[80px] w-full rounded-md border border-input bg-muted/5 px-3 py-2 text-xs font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring transition-all"
                  placeholder="Describe this layer for other authors..."
                  value={layer.description || ""}
                  onChange={(e) => onUpdate({ description: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label className="text-[10px] font-bold uppercase text-muted-foreground/70 tracking-tight flex items-center gap-1.5">
                    <Database className="w-3 h-3" /> Source Identifier
                  </Label>
                  <div className="px-3 py-2 rounded bg-muted/20 border font-mono text-[10px] text-muted-foreground truncate">
                    {layer.source_id || "No Source ID"}
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label className="text-[10px] font-bold uppercase text-muted-foreground/70 tracking-tight flex items-center gap-1.5">
                    <Layers className="w-3 h-3" /> Vector Layer
                  </Label>
                  <div className="px-3 py-2 rounded bg-muted/20 border font-mono text-[10px] text-muted-foreground truncate">
                    {layer.source_layer || "No Source Layer"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Technical Section: Raw Authoring Document */}
        <section className="pt-4">
          <Accordion type="single" collapsible>
            <AccordionItem value="raw-config" className="border rounded-xl bg-muted/10 overflow-hidden">
              <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/20 transition-colors">
                <div className="flex items-center gap-2">
                  <Code className="w-4 h-4 text-muted-foreground" />
                  <span className="text-[11px] font-bold uppercase tracking-tight">Raw Authoring Document</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="p-3 bg-muted/80 rounded border font-mono text-[10px] leading-relaxed text-foreground/70 overflow-x-auto">
                  <pre>{JSON.stringify(layer, null, 2)}</pre>
                </div>
                <div className="mt-3 flex items-center gap-1.5 text-muted-foreground">
                  <Info className="w-3.5 h-3.5" />
                  <p className="text-[9px] italic">Only intended for power users and debugging purposes.</p>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </section>
      </div>
    </div>
  )
}
